import React, { useState } from "react";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  IContextualMenuItem,
  PrimaryButton,
  SearchBox,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import ChatHistoryList from "./ChatHistoryList";
import { Conversation } from "../../api/models";
import styles from "./ChatHistory.module.css";
import { CloseOutlined, SearchOutlined } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import {
  CLOSE_ICONS,
  DELETE_ICONS,
  IconButton as IconButtonCustom,
  SEARCH_ICONS,
} from "../IconButton";

interface ChatHistoryPanelProps {
  // Panel state
  showDeletePopup: boolean;
  showHistoryPanel: boolean;
  onClosePanel: () => void;
  setshowDeletePopup: (show: boolean) => void;

  // Chat history data
  chatHistory: Conversation[];
  displayChatHistory: Conversation[];
  isSearchActive: boolean;
  fetchingChatHistory: boolean;
  searchingChatHistory: boolean;
  fetchingConvMessages: boolean;
  selectedConvId: string;

  // Chat history actions
  handleFetchHistory: () => Promise<void>;
  handleSearchHistory: (query: string) => void;
  clearSearch: () => void;
  onSelectConversation: (id: string) => void;
  onHistoryTitleChange: (id: string, newTitle: string) => void;
  onHistoryDelete: (id: string) => void;
  onExport: (id: string, format: string) => Promise<void>;

  // Loading states
  isGenerating: boolean;
  isLoading: boolean;
  toggleToggleSpinner: (toggler: boolean) => void;

  // Clear all dialog state
  hideClearAllDialog: boolean;
  clearing: boolean;
  clearingError: boolean;
  onClearAllChatHistory: () => Promise<void>;
  onHideClearAllDialog: () => void;
}

const ChatHistoryPanel: React.FC<ChatHistoryPanelProps> = ({
  showDeletePopup,
  showHistoryPanel,
  onClosePanel,
  setshowDeletePopup,
  chatHistory,
  displayChatHistory,
  isSearchActive,
  fetchingChatHistory,
  searchingChatHistory,
  fetchingConvMessages,
  selectedConvId,
  handleFetchHistory,
  handleSearchHistory,
  clearSearch,
  onSelectConversation,
  onHistoryTitleChange,
  onHistoryDelete,
  onExport,
  isGenerating,
  isLoading,
  toggleToggleSpinner,
  hideClearAllDialog,
  clearing,
  clearingError,
  onClearAllChatHistory,
  onHideClearAllDialog,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  if (!showHistoryPanel) return null;

  const menuItems: IContextualMenuItem[] = [
    {
      key: "clearAll",
      text: "Clear all chat history",
      disabled:
        !chatHistory.length ||
        isLoading ||
        fetchingConvMessages ||
        fetchingChatHistory,
      iconProps: {},
      onRenderIcon: () => <DeleteOutlined fontSize="small" />,
    },
  ];

  const clearAllDialogContentProps = {
    type: DialogType.close,
    title: !clearingError
      ? "Are you sure you want to clear all chat history?"
      : "Error deleting all of chat history",
    closeButtonAriaLabel: "Close",
    subText: !clearingError
      ? "All chat history will be permanently removed."
      : "Please try again. If the problem persists, please contact the site administrator.",
  };

  const modalProps = {
    titleAriaId: "labelId",
    subtitleAriaId: "subTextId",
    isBlocking: true,
    styles: { main: { maxWidth: 450 } },
  };

  const onSearchChange = (
    _event?: React.ChangeEvent<HTMLInputElement>,
    newValue?: string,
  ) => {
    const value = newValue ?? "";
    setSearchValue(value);
    if (value.trim().length < 3) {
      clearSearch();
      return;
    }
    handleSearchHistory(value);
  };

  const onClearSearchValue = () => {
    setSearchValue("");
    clearSearch();
  };

  const onCloseSearch = () => {
    onClearSearchValue();
    setIsSearchVisible(false);
  };

  return (
    <section
      className={styles.historyContainer}
      data-is-scrollable
      aria-label="chat history panel"
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        wrap
        aria-label="chat history header"
        className={styles.historyHeaderRow}
      >
        <StackItem>
          <Text role="heading" aria-level={2} className={styles.historyTitle}>
            Chat history
          </Text>
        </StackItem>

        <Stack horizontal className={styles.historyPanelTopRightButtons}>
          <IconButtonCustom
            icons={SEARCH_ICONS}
            size="small"
            className={styles.headerIconButton}
            disabled={
              !chatHistory.length ||
              isLoading ||
              fetchingConvMessages ||
              fetchingChatHistory
            }
            onClick={() => setIsSearchVisible(!isSearchVisible)}
            aria-label="Search in chat history"
            label="Search in chat history"
          />
          <IconButtonCustom
            icons={DELETE_ICONS}
            size="small"
            className={styles.headerIconButton}
            disabled={
              !chatHistory.length ||
              isLoading ||
              fetchingConvMessages ||
              fetchingChatHistory
            }
            onClick={() => {
              setshowDeletePopup(true);
              onHideClearAllDialog();
            }}
            aria-label="clear all chat history"
            label="Clear all chat history"
          />
          <IconButtonCustom
            icons={CLOSE_ICONS}
            size="small"
            className={styles.headerIconButton}
            aria-label="hide button"
            label="Hide chat history"
            onClick={onClosePanel}
          />
        </Stack>

        {isSearchVisible && (
          <Stack
            horizontal
            className={styles.historySearchRow}
            aria-label="chat history search"
          >
            <StackItem grow className={styles.historySearchBoxContainer}>
              <SearchBox
                className={styles.historySearchBox}
                placeholder="Search..."
                value={searchValue}
                onChange={onSearchChange}
                onClear={onClearSearchValue}
                styles={{ root: { width: "100%" } }}
              />
            </StackItem>
            <IconButton
              size="small"
              className={styles.headerIconButton}
              onClick={onCloseSearch}
              aria-label="Close search"
              title="Close search"
            >
              <CloseOutlined fontSize="small" sx={{ color: "black" }} />
            </IconButton>
          </Stack>
        )}
      </Stack>
      <Stack
        aria-label="chat history panel content"
        style={{
          display: "flex",
          height: "100%",
          padding: "1px",
        }}
      >
        <Stack className={styles.chatHistoryListContainer}>
          <ChatHistoryList
            fetchingChatHistory={
              isSearchActive ? searchingChatHistory : fetchingChatHistory
            }
            handleFetchHistory={isSearchActive ? undefined : handleFetchHistory}
            chatHistory={displayChatHistory}
            isSearchActive={isSearchActive}
            onSelectConversation={onSelectConversation}
            selectedConvId={selectedConvId}
            onHistoryTitleChange={onHistoryTitleChange}
            onHistoryDelete={onHistoryDelete}
            onExport={onExport}
            isGenerating={isGenerating}
            toggleToggleSpinner={toggleToggleSpinner}
          />
        </Stack>
      </Stack>

      {showDeletePopup && (
        <Dialog
          hidden={hideClearAllDialog}
          onDismiss={clearing ? () => {} : onHideClearAllDialog}
          dialogContentProps={clearAllDialogContentProps}
          modalProps={modalProps}
        >
          <DialogFooter>
            {!clearingError && (
              <PrimaryButton
                onClick={onClearAllChatHistory}
                disabled={clearing}
                text="Clear All"
              />
            )}
            <DefaultButton
              onClick={onHideClearAllDialog}
              disabled={clearing}
              text={!clearingError ? "Cancel" : "Close"}
            />
          </DialogFooter>
        </Dialog>
      )}
    </section>
  );
};

export default ChatHistoryPanel;
